<template>
  <div :dir="isRtl ? 'rtl' : 'ltr'" class="min-h-screen font-sans bg-white text-slate-900 antialiased selection:bg-amber-500 selection:text-white">

    <!-- TOP NAVIGATION BAR -->
    <header class="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 transition-all">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-4">

        <!-- BRAND LOGO -->
        <a href="#" class="flex items-center gap-2 sm:gap-3 shrink-0">
          <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-amber-600 to-orange-500 flex items-center justify-center text-white shadow-md shadow-orange-500/20">
            <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
            </svg>
          </div>
          <span class="text-xl sm:text-2xl font-black tracking-tight text-slate-900">Sayang <span class="text-amber-600">Biz</span></span>
        </a>

        <!-- DESKTOP NAV LINKS -->
        <nav class="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-600">
          <a href="#features" class="hover:text-amber-600 transition-colors">{{ $t('nav.features') }}</a>
          <a href="#hardware" class="hover:text-amber-600 transition-colors">{{ $t('nav.hardware') }}</a>
          <a href="#pricing" class="hover:text-amber-600 transition-colors">{{ $t('nav.pricing') }}</a>
          <a href="#business-types" class="hover:text-amber-600 transition-colors">{{ $t('nav.businessTypes') }}</a>
          <a href="#products" class="hover:text-amber-600 transition-colors">{{ $t('nav.products') }}</a>
          <a href="#help" class="hover:text-amber-600 transition-colors">{{ $t('nav.help') }}</a>
        </nav>

        <!-- RIGHT SIDE ACTIONS -->
        <div class="flex items-center gap-1.5 sm:gap-3 shrink-0">

          <!-- LANGUAGE SELECTOR DROPDOWN -->
          <div class="relative">
            <button
              @click="toggleLangDropdown"
              class="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200 whitespace-nowrap"
            >
              <svg class="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
              </svg>
              <span>{{ currentLangName }}</span>
              <svg class="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- DROPDOWN MENU -->
            <div
              v-if="langDropdownOpen"
              class="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 z-50 max-h-80 overflow-y-auto"
            >
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
                class="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-amber-50 hover:text-amber-600 flex items-center justify-between transition-colors"
                :class="{ 'text-amber-600 font-bold bg-amber-50/50': $i18n.locale === lang.code }"
              >
                <span>{{ lang.name }}</span>
                <span class="text-[10px] uppercase text-slate-400 font-bold">{{ lang.code }}</span>
              </button>
            </div>
          </div>

          <!-- SIGN IN BUTTON -->
          <a
            href="https://app.sayang.biz/login"
            target="_blank"
            class="hidden sm:inline-flex px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 hover:text-amber-600 transition-colors whitespace-nowrap"
          >
            {{ $t('nav.signIn') }}
          </a>

          <!-- START FOR FREE BUTTON -->
          <a
            href="https://app.sayang.biz/signup"
            target="_blank"
            class="inline-flex items-center justify-center px-3.5 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 active:bg-amber-800 rounded-full shadow-md shadow-amber-600/20 transition-all hover:scale-105 whitespace-nowrap"
          >
            {{ $t('nav.startForFree') }}
          </a>
        </div>
      </div>
    </header>

    <!-- HERO SECTION (Loyverse Soft Green Style #eef7f2) -->
    <section class="relative bg-[#eef7f2] py-12 sm:py-20 lg:py-28 overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          <!-- LEFT HERO CONTENT -->
          <div class="lg:col-span-7 text-left">
            <h1 class="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.15] mb-4 sm:mb-6 tracking-tight">
              {{ $t('hero.title') }}
            </h1>
            <p class="text-base sm:text-xl text-slate-600 font-medium leading-relaxed mb-6 sm:mb-8 max-w-2xl">
              {{ $t('hero.description') }}
            </p>

            <div class="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3.5 mb-8 sm:mb-10">
              <a
                href="https://app.sayang.biz/signup"
                target="_blank"
                class="inline-flex items-center justify-center px-8 py-3.5 text-sm sm:text-base font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-full shadow-lg shadow-amber-600/30 transition-all hover:scale-105 text-center"
              >
                {{ $t('hero.cta') }}
              </a>

              <!-- PLAY STORE BADGE -->
              <a
                href="https://play.google.com/store/apps/details?id=org.usmanheart.sayang_seller"
                target="_blank"
                class="inline-flex items-center justify-center gap-3 px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl shadow-md transition-all hover:scale-105"
              >
                <svg class="w-6 h-6 text-emerald-400 fill-current shrink-0" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L18.81,13.12C19.86,12.5 19.86,11.5 18.81,10.88L16.81,8.88L14.83,10.86L14.83,13.14L16.81,15.12M14.28,14.56L4.85,24L14.28,14.56M4.85,0L14.28,9.44L4.85,0Z"/>
                </svg>
                <div class="text-left">
                  <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">GET IT ON</div>
                  <div class="text-xs font-extrabold text-white">Google Play</div>
                </div>
              </a>

              <!-- IOS COMING SOON BADGE -->
              <div class="inline-flex items-center justify-center gap-3 px-5 py-3 bg-white/90 border border-slate-200 text-slate-700 rounded-2xl shadow-sm">
                <svg class="w-6 h-6 text-slate-800 fill-current shrink-0" viewBox="0 0 24 24">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.09,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                </svg>
                <div class="text-left">
                  <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">APPLE APP</div>
                  <div class="text-xs font-bold text-slate-600">{{ $t('hero.iosComingSoon') }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT HERO SHOWCASE IMAGE -->
          <div class="lg:col-span-5 relative">
            <div class="bg-white p-3 sm:p-4 rounded-3xl shadow-xl sm:shadow-2xl border border-slate-200/60 transform rotate-1 hover:rotate-0 transition-transform duration-500">
              <div class="bg-slate-900 rounded-2xl p-4 sm:p-6 text-white shadow-inner">

                <!-- HEADER BAR MOCK -->
                <div class="flex items-center justify-between border-b border-slate-800 pb-3 sm:pb-4 mb-4 sm:mb-6">
                  <div class="flex items-center gap-1.5">
                    <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                    <div class="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                  </div>
                  <span class="text-[10px] sm:text-xs font-bold text-amber-500 bg-amber-500/10 px-2.5 py-0.5 rounded-full">Sayang Biz Live POS</span>
                </div>

                <!-- CARDS MOCK -->
                <div class="grid grid-cols-2 gap-2.5 mb-4 sm:mb-6">
                  <div class="bg-slate-800/80 p-3 sm:p-4 rounded-xl border border-slate-700">
                    <div class="text-[10px] sm:text-xs font-semibold text-slate-400 mb-0.5 sm:mb-1">Today Sales</div>
                    <div class="text-base sm:text-xl font-bold text-emerald-400">Rp 1.189.000</div>
                  </div>
                  <div class="bg-slate-800/80 p-3 sm:p-4 rounded-xl border border-slate-700">
                    <div class="text-[10px] sm:text-xs font-semibold text-slate-400 mb-0.5 sm:mb-1">Items Sold</div>
                    <div class="text-base sm:text-xl font-bold text-amber-400">142 items</div>
                  </div>
                </div>

                <!-- HOURLY BARS MOCK -->
                <div class="bg-slate-800/50 p-3 sm:p-4 rounded-xl border border-slate-700/50 mb-3 sm:mb-4">
                  <div class="text-[10px] sm:text-xs font-bold text-slate-300 mb-2 sm:mb-3">Sales Rhythm</div>
                  <div class="h-16 sm:h-20 flex items-end justify-between gap-1">
                    <div class="w-full bg-emerald-500/30 rounded-t h-[30%]"></div>
                    <div class="w-full bg-emerald-500/50 rounded-t h-[50%]"></div>
                    <div class="w-full bg-emerald-500 rounded-t h-[90%]"></div>
                    <div class="w-full bg-emerald-500/70 rounded-t h-[65%]"></div>
                    <div class="w-full bg-emerald-500/40 rounded-t h-[40%]"></div>
                    <div class="w-full bg-emerald-500/60 rounded-t h-[55%]"></div>
                  </div>
                </div>

                <div class="flex items-center justify-between text-[10px] sm:text-xs font-bold text-slate-400">
                  <span>Fast Checkout</span>
                  <span class="text-emerald-400">Dynamic QRIS Ready</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- GLOBAL MERCHANT COMMUNITY BANNER -->
    <section class="py-12 sm:py-16 bg-white border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p class="text-xs font-black text-amber-600 tracking-widest uppercase mb-2 sm:mb-3">{{ $t('stats.title') }}</p>
        <h2 class="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight max-w-3xl mx-auto">
          {{ $t('stats.subtitle') }}
        </h2>
      </div>
    </section>

    <!-- FEATURES SECTION -->
    <section id="features" class="py-16 sm:py-24 bg-slate-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span class="text-xs font-black text-amber-600 tracking-widest uppercase mb-2 block">{{ $t('features.tagline') }}</span>
          <h2 class="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
            {{ $t('features.title') }}
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">

          <!-- FEATURE 1 -->
          <div class="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div class="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center mb-6">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">{{ $t('features.posTitle') }}</h3>
            <p class="text-sm text-slate-600 font-medium leading-relaxed">{{ $t('features.posDesc') }}</p>
          </div>

          <!-- FEATURE 2 -->
          <div class="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div class="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-6">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">{{ $t('features.analyticsTitle') }}</h3>
            <p class="text-sm text-slate-600 font-medium leading-relaxed">{{ $t('features.analyticsDesc') }}</p>
          </div>

          <!-- FEATURE 3 -->
          <div class="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div class="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-600 flex items-center justify-center mb-6">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">{{ $t('features.inventoryTitle') }}</h3>
            <p class="text-sm text-slate-600 font-medium leading-relaxed">{{ $t('features.inventoryDesc') }}</p>
          </div>

          <!-- FEATURE 4 -->
          <div class="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div class="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center mb-6">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">{{ $t('features.staffTitle') }}</h3>
            <p class="text-sm text-slate-600 font-medium leading-relaxed">{{ $t('features.staffDesc') }}</p>
          </div>

        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="bg-slate-900 text-white py-10 sm:py-12 border-t border-slate-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-amber-600 flex items-center justify-center text-white font-black">S</div>
          <span class="text-xl font-extrabold tracking-tight">Sayang <span class="text-amber-500">Biz</span></span>
        </div>
        <p class="text-xs text-slate-400 font-medium">{{ $t('footer.description') }}</p>
        <p class="text-xs text-slate-500 font-medium">© {{ new Date().getFullYear() }} Sayang Biz. {{ $t('footer.rights') }}</p>
      </div>
    </footer>

    <!-- STICKY BOTTOM AD BANNER SLOT -->
    <div class="sticky bottom-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 py-2 text-center">
      <div class="max-w-7xl mx-auto px-4 flex justify-center">
        <ins
          class="adsbygoogle"
          style="display:inline-block;width:320px;height:50px"
          data-ad-client="ca-pub-2115624265902870"
          data-ad-slot="auto"
        ></ins>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'MainPage',
  data() {
    return {
      langDropdownOpen: false,
      languages: [
        { code: 'ar', name: 'العربية' },
        { code: 'de', name: 'Deutsch' },
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Español' },
        { code: 'et', name: 'Eesti' },
        { code: 'fr', name: 'Français' },
        { code: 'hi', name: 'हिन्दी' },
        { code: 'id', name: 'Bahasa Indonesia' },
        { code: 'ja', name: '日本語' },
        { code: 'ko', name: '한국어' },
        { code: 'lo', name: 'ພາສາລາວ' },
        { code: 'ms', name: 'Bahasa Melayu' },
        { code: 'pt', name: 'Português' },
        { code: 'ru', name: 'Русский' },
        { code: 'th', name: 'ไทย' },
        { code: 'vi', name: 'Tiếng Việt' },
        { code: 'zh', name: '中文' },
      ]
    }
  },
  mounted() {
    this.detectLanguage()
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch (_) {}
  },
  computed: {
    currentLangName() {
      const current = this.languages.find(l => l.code === this.$i18n.locale)
      return current ? current.name : 'English'
    },
    isRtl() {
      return this.$i18n.locale === 'ar'
    }
  },
  methods: {
    detectLanguage() {
      const savedLang = localStorage.getItem('sayang_biz_lang')
      if (savedLang && this.languages.some(l => l.code === savedLang)) {
        this.applyLanguage(savedLang)
        return
      }

      const browserLangs = navigator.languages || [navigator.language || navigator.userLanguage]
      let detectedCode = 'en'

      for (const langStr of browserLangs) {
        if (!langStr) continue
        const primaryCode = langStr.split('-')[0].toLowerCase()
        if (this.languages.some(l => l.code === primaryCode)) {
          detectedCode = primaryCode
          break
        }
      }

      this.applyLanguage(detectedCode)
    },
    toggleLangDropdown() {
      this.langDropdownOpen = !this.langDropdownOpen
    },
    changeLanguage(code) {
      localStorage.setItem('sayang_biz_lang', code)
      this.applyLanguage(code)
      this.langDropdownOpen = false
    },
    applyLanguage(code) {
      this.$i18n.locale = code
      if (code === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl')
      } else {
        document.documentElement.setAttribute('dir', 'ltr')
      }
    }
  }
}
</script>
